import { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Grid,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import {
    Add as AddIcon,
    Save as SaveIcon,
} from '@mui/icons-material';
import { getGlobalConfig, updateTableExclusions } from '../../services/api';

function TableExclusionsConfig({ tableExclusions, onUpdate, showNotification }) {
    const [sourceTables, setSourceTables] = useState(tableExclusions?.source || []);
    const [targetTables, setTargetTables] = useState(tableExclusions?.target || []);
    const [newSourceTable, setNewSourceTable] = useState('');
    const [newTargetTable, setNewTargetTable] = useState('');
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    const addItem = (value, items, setItems, clearValue) => {
        const nextValue = value.trim();
        if (!nextValue || items.some((item) => item.toLowerCase() === nextValue.toLowerCase())) {
            return;
        }
        setItems([...items, nextValue]);
        clearValue('');
        setHasChanges(true);
    };

    const removeItem = (value, items, setItems) => {
        setItems(items.filter((item) => item !== value));
        setHasChanges(true);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await updateTableExclusions({
                description: tableExclusions?.description || 'Tables that should be proposed for removal during dropped-table review. Source applies to legacy uploads and target applies to catalyst uploads.',
                source: sourceTables,
                target: targetTables,
            });
            const config = await getGlobalConfig();
            onUpdate(config);
            setHasChanges(false);
            showNotification('Table exclusions saved successfully');
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to save table exclusions', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setSourceTables(tableExclusions?.source || []);
        setTargetTables(tableExclusions?.target || []);
        setHasChanges(false);
    };

    const renderColumn = ({
        title,
        count,
        inputValue,
        onInputChange,
        onAdd,
        items,
        onDelete,
        addPlaceholder,
        emptyMessage,
    }) => (
        <Paper sx={{ p: 2, height: '100%', minHeight: 400 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                {title} ({count})
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                    size="small"
                    placeholder={addPlaceholder}
                    value={inputValue}
                    onChange={(event) => onInputChange(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            onAdd();
                        }
                    }}
                    sx={{ flex: 1 }}
                />
                <Button
                    variant="outlined"
                    size="small"
                    onClick={onAdd}
                    disabled={!inputValue.trim()}
                >
                    <AddIcon />
                </Button>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    maxHeight: 300,
                    overflowY: 'auto',
                    p: 1,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                }}
            >
                {items.map((item) => (
                    <Chip
                        key={item}
                        label={item}
                        size="small"
                        onDelete={() => onDelete(item)}
                        sx={{ fontSize: '0.75rem' }}
                    />
                ))}
                {items.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                        {emptyMessage}
                    </Typography>
                )}
            </Box>
        </Paper>
    );

    return (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h6" fontWeight={600}>
                        Table Exclusions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {tableExclusions?.description || 'Tables added here are preloaded into dropped-table review. Source applies to legacy uploads and target applies to catalyst uploads.'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {hasChanges && (
                        <Button variant="outlined" color="inherit" onClick={handleReset}>
                            Reset
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={!hasChanges || saving}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Box>
            </Box>

            {hasChanges && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    You have unsaved changes. Click "Save Changes" to persist them.
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    {renderColumn({
                        title: 'Source Tables',
                        count: sourceTables.length,
                        inputValue: newSourceTable,
                        onInputChange: setNewSourceTable,
                        onAdd: () => addItem(newSourceTable, sourceTables, setSourceTables, setNewSourceTable),
                        items: sourceTables,
                        onDelete: (value) => removeItem(value, sourceTables, setSourceTables),
                        addPlaceholder: 'Add source table...',
                        emptyMessage: 'No source tables defined',
                    })}
                </Grid>

                <Grid item xs={12} md={6}>
                    {renderColumn({
                        title: 'Target Tables',
                        count: targetTables.length,
                        inputValue: newTargetTable,
                        onInputChange: setNewTargetTable,
                        onAdd: () => addItem(newTargetTable, targetTables, setTargetTables, setNewTargetTable),
                        items: targetTables,
                        onDelete: (value) => removeItem(value, targetTables, setTargetTables),
                        addPlaceholder: 'Add target table...',
                        emptyMessage: 'No target tables defined',
                    })}
                </Grid>
            </Grid>
        </Box>
    );
}

export default TableExclusionsConfig;
